import {Button, Dialog, Icon} from '@startlibs/components'
import {Errors, SimpleCheckbox, WithForm} from '@startlibs/form'
import {formatDate, getColor} from '@startlibs/utils';
import {lighten, transparentize, darken, desaturate} from 'polished';
import {useToggle} from '@startlibs/core'
import React, {useState, useRef, useEffect, useContext} from 'react'
import _ from 'lodash/fp'
import qq from 'fine-uploader'
import styled, {css} from 'styled-components'

import {dicomDateToDate} from '../utils'
import {useViewRecords, useViewStudy} from '../service/utils/downloadFile'
import {UploaderConfigContext} from "../service/UploaderConfigContext";
import {RecordFormat} from "../service/utils";
import {DicomStudy} from "../enums/RecordFormat";
import {useRecordsSelector} from "../service/hooks/useRecords";
import { useUIDataSelector } from '../service/hooks/useUIDataSelector';
import { getFilenameFromRecord } from './recordGroup/RecordRowActions';
import { jwtPostFetcher } from '../utils/authFetch';
import { PROVIDER } from '../enums/UserRoles';
import {FormattedMessage, useIntl} from "react-intl";
import {defaultDateFormat} from "../utils/intlDateFormat";

const isSafari = qq.safari();

const LaunchViewerButton = styled(Button)`
  margin-left: auto;
  flex-shrink: 0;
  ${props => props.isGroupButton && css`
    margin-top: 0;
    margin-bottom: .25rem;
    border: 0;
    :hover, :focus, :active {
      border: 0;
      box-shadow: none;
      :after {
        display: none;
      }
    }
  `}
`

const StudyItemStyled = styled.div`
  position: relative;
  background: ${getColor('gray240')};
  border-bottom: 1px solid ${getColor('gray210')};
  padding: 0.75rem 4rem 0.75rem 1.25rem;
  cursor: pointer;
  :last-child {
    border-bottom: none;
  }
  :hover {
    background: ${props => transparentize(0.85, getColor('main')(props))};
  }
  ${props => props.isChecked && css`
    background: ${getColor('main')};
    color: white;
    .authorizationAlert {
      color: #ffb9b9 !important;
    }
    :hover {
      background: ${getColor('main')};
    }
  `}
  ${SimpleCheckbox} {
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
  }
`
const StudiesListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  p {
    margin-bottom: 0;
  }
  span {
    margin-left: 1rem;
    color: ${getColor('gray180')};
  }
`
const StudiesList = styled.div`
  border: 1px solid ${getColor('gray210')};
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 1.5rem;
  > div {
    max-height: 16rem;
    overflow: auto;
  }
`

const StudyTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 0.25rem;
`

const StudyDetails = styled.div`
  margin-top: 2px;
  font-size: 11px;
  span + span {
    margin-left: 1rem;
  }
`

export const InfoBox = styled.div`
  background: ${getColor('gray240')};
  border: 1px solid ${getColor('gray210')};
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  width: 100%;
  color: ${getColor('gray120')};
  ${props => props.lightBlue && css`
    background: ${desaturate(0.65, lighten(0.545, getColor('main')(props)))};
    border: 1px solid ${props => transparentize(0.75, darken(0.012, desaturate(0.4, getColor('main')(props))))};
    color: ${getColor('main')};
  `}
  ${props => props.error && css`
    background: ${props => lighten(0.43, props.theme.colors.alert)};
    border-color: ${props => lighten(0.32, props.theme.colors.alert)};
    color: ${getColor('alert')};
  `}
  ${props => props.withIcon && css`
    display: flex;
    align-items: center;
    ${Icon} {
      font-size: 16px;
      margin-right: .5rem;
      font-weight: 400;
    }
  `}
`

export const ViewAllButton = ({isGroupButton}) => {
  const intl = useIntl()

  const groups = useUIDataSelector('groups')
  const notClassGroup = groups.find(group => group.id === 'notclass')
  // prevent this error item.includes is not a function from happen
  const dicomsInNotClassGroup = notClassGroup?.items?.filter(item => item && item != null && item?.length > 0 && item?.includes('.'))
  // const dicomRecords = useRecordsSelector(_.filter(_.matchesProperty('recordFormat',DicomStudy))).filter(dicom => !dicomsInNotClassGroup.includes(dicom.key))
  const dicomRecords = useRecordsSelector(_.filter(dicom => {
    return (dicom.recordFormat === DicomStudy || dicom?.info?.type === DicomStudy) && !dicomsInNotClassGroup?.includes(dicom.key)
  }));
  const [selectedAttachments, setSelectedAttachments] = useState([])
  const config = useContext(UploaderConfigContext)
  const [openViewerLink, viewerIsLoading] = useViewRecords(config, RecordFormat.DicomStudy, (dicomRecords.length > 4 ? selectedAttachments : dicomRecords))

  const {
    requestId,
    role,
    worklistViewerJwt: expertViewJwt,
  } = config
  
  const openViewerAndLog = () => {
    openViewerLink()
    const records = dicomRecords.length > 4 ? selectedAttachments : dicomRecords
    if(role !== PROVIDER){
      const descriptions = records.map((record) => {
        try{
          return getFilenameFromRecord(intl, record)
        }catch(e){
          console.log(e)
        }
      })
      jwtPostFetcher(expertViewJwt)(`/api/audit/study/viewRequested?requestId=${requestId}`, {
        recordDescriptions: descriptions.filter(v => v)
      })
    }
   
  }
  const dialog = useToggle()
  const infoBox = useToggle()

  useEffect(() => {
    if (selectedAttachments.length < 4 && infoBox.isOpen) {
      infoBox.close()
    }
  },[selectedAttachments])

  useEffect(() => {
    setSelectedAttachments(_.filter(attachment => dicomRecords.indexOf(attachment)>=0))
  },[dicomRecords])

  if (isSafari) {
    return null
  }

  return dicomRecords?.length >= 1
    ? <>
      <LaunchViewerButton
        onClick={dicomRecords?.length <= 4 ? openViewerAndLog : dialog.open}
        isLoading={viewerIsLoading}
        small
        outline
        icon="view"
        isGroupButton={isGroupButton}
      ><FormattedMessage
        defaultMessage="View {seeAll, select, true {all} other {}} DICOM studies"
        description="Uploader, view dicom button"
        values={{seeAll: dicomRecords?.length <= 4}}
      /></LaunchViewerButton>
      {
        dialog.isOpen &&
        <WithForm
          alwaysSave
          action={() => {
            openViewerAndLog()
          }}
          onSuccess={() => {
            setSelectedAttachments([]);
            dialog.close()
          }}
          preValidation={() => selectedAttachments.length < 1 && {"":intl.formatMessage({
            defaultMessage:"You must select at least one DICOM study to open in the web viewer.",
            description:"Uploader, view dicom error, needs to select at least 1"
          })}}
        >{form =>
          <Dialog
            title={intl.formatMessage({
              defaultMessage: "Select studies to open on web viewer",
              description: "Uploader, view dicom dialog title"
            })}
            closeDialog={dialog.close}
            footer={<>
              <Button onClick={dialog.close}>
                <FormattedMessage defaultMessage="Cancel" description="Cancel button"/>
              </Button>
              <Button onClick={form.submitForm} highlight><FormattedMessage
                defaultMessage="Launch web viewer"
                description="Uploader, view dicom dialog, launch button"
              /></Button>
            </>}
          >
            <p><FormattedMessage
              defaultMessage={`You are about to launch the DICOM studies web viewer.
              Please select the studies you would like to open
              (there is a 4 studies per session technical limitation).`}
              description="Uploader, view dicom dialog, info"
            /></p>
            <StudiesListHeader>
              <p><strong><FormattedMessage
                defaultMessage="Select DICOM studies to open on web viewer:"
                description="Uploader, view dicom dialog, select studies info"
              /></strong></p>
              <span><FormattedMessage
                defaultMessage="{selectedAttachments, plural, =0 {No studies} one {# study} other {# studies}} selected"
                description="Uploader, view dicom dialog, selected studies count"
                values={{selectedAttachments: selectedAttachments.length}}
                /></span>
            </StudiesListHeader>
            <StudiesList>
              <div>
                {dicomRecords.map(attachment => <AttachmentItem
                attachment={attachment}
                isChecked={selectedAttachments?.indexOf(attachment) >= 0}
                toggle={() => setSelectedAttachments(attachments =>
                  attachments.indexOf(attachment) >= 0
                  ? attachments.filter(a => a !== attachment)
                  : attachments.length >= 4
                    ? (infoBox.open() && false) || attachments
                    : attachments.concat(attachment)
                )}
                />)}
              </div>
            </StudiesList>
            {
              infoBox.isOpen &&
              <InfoBox lightBlue><FormattedMessage
                defaultMessage={ `Due to a technical limitation the viewer only supports opening
                4 studies per session. If you would like to view more studies
                consider opening them separately in other browser windows or tabs.`}
                description="Uploader, view dicom dialog, limitation info box"
              />
              </InfoBox>
            }
            <Errors/>
          </Dialog>
        }</WithForm>
      }
    </>
    : null
}


const AttachmentItem = ({attachment, isChecked, toggle}) => {
  // const isChecked = values.indexOf(release.id) >= 0
  const intl = useIntl()
  const ref = useRef()
  useEffect(() => {
    if (isChecked && ref.current) {
      ref.current.scrollIntoView({block: "center"})
    }
  }, [])

  const studyDate = dicomDateToDate(attachment.studyDate) || dicomDateToDate(attachment.info.studyDate) 
  const studyDescription = attachment.studyDescription || attachment.info.studyDescription
  const modalities = attachment.modalities || attachment.info.modalities
  const patientName = attachment.patientName || attachment.info.patientName

  return <StudyItemStyled ref={ref} onClick={toggle} isChecked={isChecked}>

    <StudyTitle>{studyDescription} {!isNaN(studyDate) && `(${formatDate(studyDate, defaultDateFormat(intl,"-"))})`}</StudyTitle>
    <StudyDetails>
      <span><FormattedMessage
        defaultMessage="Modalities"
        description="Uploader, dicom attachment, modalities label"
        />: {modalities.join(", ")}</span>
      <span><FormattedMessage
        defaultMessage="Patient name"
        description="Uploader, dicom attachment, patient name label"
        />: {patientName}</span>
    </StudyDetails>
    <div onClick={e => e.stopPropagation()}><SimpleCheckbox
      raw
      setValue={toggle}
      value={isChecked}
    /></div>
  </StudyItemStyled>
}
